import { AUTH_BASE_URL } from '../config/api';

const API_BASE_URL = AUTH_BASE_URL;

/**
 * Convert speech to text using OpenAI Whisper
 */
export const transcribeAudio = async (audioBlob, language = null) => {
  const formData = new FormData();
  formData.append('audio', audioBlob, 'recording.webm');
  
  if (language) {
    formData.append('language', language);
  }

  const response = await fetch(`${API_BASE_URL}/api/audio/transcribe`, {
    method: 'POST',
    credentials: 'include',
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Network error' }));
    throw new Error(error.error || 'Failed to transcribe audio');
  }

  return response.json();
};

/**
 * Convert text to speech using OpenAI TTS
 */
export const synthesizeSpeech = async (text, voice = 'alloy', format = 'mp3') => {
  const response = await fetch(`${API_BASE_URL}/api/audio/synthesize`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify({ text, voice, format }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Network error' }));
    throw new Error(error.error || 'Failed to synthesize speech');
  }

  // Return audio buffer
  return response.arrayBuffer();
};

/**
 * Get available voice options
 */
export const getAvailableVoices = async () => {
  const response = await fetch(`${API_BASE_URL}/api/audio/voices`, {
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Failed to fetch available voices');
  }

  return response.json();
};

/**
 * Audio-enabled chat completion
 */
export const audioChat = async (messages, model = 'gpt-4o-audio-preview', voice = 'alloy', includeAudioResponse = true) => {
  const response = await fetch(`${API_BASE_URL}/api/audio/chat-audio`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify({ 
      messages, 
      model, 
      voice, 
      includeAudioResponse 
    }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Network error' }));
    throw new Error(error.error || 'Failed to get audio chat response');
  }

  return response.json();
};

/**
 * Audio recording utilities
 */
export class AudioRecorder {
  constructor() {
    this.mediaRecorder = null;
    this.recordedChunks = [];
    this.stream = null;
  }

  async startRecording() {
    try {
      // Request microphone access
      this.stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        } 
      });

      // Create MediaRecorder
      this.mediaRecorder = new MediaRecorder(this.stream, {
        mimeType: 'audio/webm;codecs=opus'
      });

      this.recordedChunks = [];

      // Handle data available
      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this.recordedChunks.push(event.data);
        }
      };

      // Start recording
      this.mediaRecorder.start(100); // Collect data every 100ms

      return true;
    } catch (error) {
      console.error('Failed to start recording:', error);
      throw new Error('Microphone access denied or not available');
    }
  }

  async stopRecording() {
    return new Promise((resolve, reject) => {
      if (!this.mediaRecorder || this.mediaRecorder.state === 'inactive') {
        reject(new Error('No active recording'));
        return;
      }

      this.mediaRecorder.onstop = () => {
        // Create blob from recorded chunks
        const blob = new Blob(this.recordedChunks, { type: 'audio/webm' });
        
        // Stop all tracks
        if (this.stream) {
          this.stream.getTracks().forEach(track => track.stop());
        }

        resolve(blob);
      };

      this.mediaRecorder.stop();
    });
  }

  isRecording() {
    return this.mediaRecorder && this.mediaRecorder.state === 'recording';
  }

  cleanup() {
    if (this.mediaRecorder && this.mediaRecorder.state === 'recording') {
      this.mediaRecorder.stop();
    }
    
    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop());
    }
  }
}

/**
 * Audio playback utilities
 */
export class AudioPlayer {
  constructor() {
    this.audio = null;
    this.isPlaying = false;
  }

  async playAudioBuffer(audioBuffer) {
    try {
      // Convert buffer to blob
      const blob = new Blob([audioBuffer], { type: 'audio/mpeg' });
      const audioUrl = URL.createObjectURL(blob);

      // Create and play audio element
      this.audio = new Audio(audioUrl);
      this.isPlaying = true;

      // Set up event listeners
      this.audio.onended = () => {
        this.isPlaying = false;
        URL.revokeObjectURL(audioUrl);
      };

      this.audio.onerror = (error) => {
        this.isPlaying = false;
        URL.revokeObjectURL(audioUrl);
        console.error('Audio playback error:', error);
      };

      await this.audio.play();
      return true;
    } catch (error) {
      this.isPlaying = false;
      console.error('Failed to play audio:', error);
      throw error;
    }
  }

  async playText(text, voice = 'alloy') {
    try {
      const audioBuffer = await synthesizeSpeech(text, voice);
      await this.playAudioBuffer(audioBuffer);
    } catch (error) {
      console.error('Failed to play text as speech:', error);
      throw error;
    }
  }

  stop() {
    if (this.audio && !this.audio.paused) {
      this.audio.pause();
      this.audio.currentTime = 0;
      this.isPlaying = false;
    }
  }

  isCurrentlyPlaying() {
    return this.isPlaying;
  }
}

// Export singleton instances for convenience
export const audioRecorder = new AudioRecorder();
export const audioPlayer = new AudioPlayer();